import { useState, useEffect, useCallback, useRef } from "react";
import { submitRegulaLegalIdVerification, getLegalIdVerificationStatus, patchLegalIdVerification, ApiError } from "../../shared/api";
import {
  addAPIBreadcrumb,
  addVerificationFlowBreadcrumb,
} from "../../shared/utils/sentryBreadcrumbs";
import {
  setVerificationUser,
  trackFlowError,
} from "../../shared/utils/sentryTracking";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { useRenderedSignal } from "../legalIdVerification/useRenderedSignal";
import { isTokenExpiringSoon } from "../../shared/utils/tokenUtils";
import { logger } from "../../shared/utils/logger";
import { setAnalyticsIdentity, setAnalyticsEnabled, resolveReferenceType, type ParentService } from "../../shared/analytics/analyticsClient";
import { trackViewed, trackOutcomePassed, trackOutcomeFailed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS, type OutcomeSection } from "../../shared/analytics/documentCaptureEvents";
import type {
  DocumentSuccessPayload,
  DocumentErrorPayload,
  DocumentFailedApi,
  VerificationCompleteMessage,
  VerificationErrorMessage,
} from "../../shared/types";
import type { LegalIdVerificationResponse } from "../../shared/types/verification";

export type RegulaLegalIdVerificationState = "initializing" | "ready" | "capturing" | "processing" | "confirming" | "completed" | "error";

/** Maps HTTP status code to the appropriate DocumentErrorCode */
function errorCodeFromStatus(statusCode: number | undefined): DocumentErrorPayload["errorCode"] {
  if (statusCode === 401) return "session_invalid";
  if (statusCode === 400) return "session_invalid";
  if (statusCode !== undefined && statusCode >= 500) return "internal_error";
  return "network_error";
}

const OUTCOME_SECTION_BY_STAGE: Record<RegulaLegalIdVerificationState, OutcomeSection | null> = {
  initializing: DOCUMENT_CAPTURE_SECTIONS.INIT,
  ready: null,
  capturing: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE,
  processing: DOCUMENT_CAPTURE_SECTIONS.PROCESSING,
  confirming: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION,
  completed: null,
  error: null,
};

function trackTerminalFailure(stage: RegulaLegalIdVerificationState, errorCode: DocumentErrorPayload["errorCode"]): void {
  const section = OUTCOME_SECTION_BY_STAGE[stage];
  if (!section) return;
  trackOutcomeFailed(section, errorCode);
}

/**
 * Custom hook to manage Regula legal ID verification flow.
 *
 * Mirrors useLegalIdVerification (Veriff), but follows athena's REGULA **UPLOAD** model: there is
 * no vendor session and no create-on-init. Instead:
 * - Init validates the parent origin and receives the session token via postMessage (IDME_INIT),
 *   then goes straight to "ready" — no backend call yet.
 * - The document is captured client-side (Regula locator, via useRegulaEvents); the captured
 *   base64 images are then POSTed (submitCapture → POST /v1/verifications/legal-ids, UPLOAD mode).
 * - athena processes asynchronously, so we poll GET /v1/verifications/legal-ids/{id} to a terminal
 *   outcome (reusing getLegalIdVerificationStatus), show a confirmation, then PATCH-accept.
 *
 * Poll/confirm/error handling and the postMessage protocol are identical to the Veriff hook so the
 * SDK launcher and parent (IVA) see the same contract.
 */
export function useRegulaLegalIdVerification() {
  const [state, setState] = useState<RegulaLegalIdVerificationState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [parentOrigin, setParentOrigin] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{ data: LegalIdVerificationResponse } | null>(null);
  const [fallbackAvailable, setFallbackAvailable] = useState(false);
  const tokenExpiresAtRef = useRef<number | null>(null);
  const externalReferenceIdRef = useRef<string | null>(null);

  useRenderedSignal(state !== "initializing", parentOrigin);

  // Latest state, readable synchronously from callbacks without stale-closure issues.
  const stateRef = useRef<RegulaLegalIdVerificationState>("initializing");

  // Sets React state AND stateRef synchronously — see useLegalIdVerification.ts for the
  // full rationale. Aliased back onto the exposed `setState` name below.
  const setFlowState = useCallback((next: RegulaLegalIdVerificationState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  // Set user context in Sentry when verification ID is available
  useEffect(() => {
    if (verificationId) {
      setVerificationUser(verificationId, sessionToken?.substring(0, 8));
    }
  }, [verificationId, sessionToken]);

  // Track state transitions as breadcrumbs
  useEffect(() => {
    addVerificationFlowBreadcrumb(state, verificationId);
  }, [state, verificationId]);

  const initializeRegulaLegalIdVerification = async () => {
    try {
      if (!document.referrer) {
        throw new Error("Cannot determine parent origin - document.referrer is empty");
      }

      const origin = new URL(document.referrer).origin;
      const hostname = new URL(document.referrer).hostname;

      const allowedDomains = getAllowedDomains();
      const isAllowedDomain = allowedDomains.some(domain =>
        hostname === domain || hostname.endsWith(`.${domain}`)
      );

      if (!isAllowedDomain) {
        throw new Error(`Invalid parent origin: ${origin}. Only IDMe domains are allowed.`);
      }

      logger.log("[RegulaLegalIdVerification] Parent origin validated:", origin);
      setParentOrigin(origin);

      const handleInitMessage = (event: MessageEvent) => {
        // Only accept the token-bearing INIT from the validated parent origin. `origin` was
        // already checked against getAllowedDomains() above, so pinning event.origin to it
        // rejects IDME_INIT messages forged by any other (e.g. malicious) frame.
        if (event.origin !== origin) {
          logger.warn("[RegulaLegalIdVerification] Ignoring IDME_INIT from unexpected origin:", event.origin);
          return;
        }
        if (!event.data || event.data.type !== "IDME_INIT") {
          return;
        }

        logger.log("[RegulaLegalIdVerification] Received initialization data via postMessage");

        const payload = event.data.payload;
        // Re-validate the origin is still an allowed domain before posting back.
        const allowedOrigin = getAllowedDomains().some(domain => {
          const h = new URL(origin).hostname;
          return h === domain || h.endsWith(`.${domain}`);
        });

        const {
          sessionToken: token,
          tokenExpiresAt,
          externalReferenceId,
          parentService,
          analyticsEnabled,
          fallbackAvailable: initFallbackAvailable,
        } = payload ?? {};

        // Bind identity + the host's rollout gate, and emit the session-started
        // milestone, as early as possible — unconditionally, before the payload-shape
        // check and the token-expiry guard below (mirrors useLegalIdVerification.ts,
        // for one consistent funnel across both Legal ID variants).
        const resolvedReferenceId = externalReferenceId || `web_${Date.now()}`;
        externalReferenceIdRef.current = resolvedReferenceId;
        const resolvedParentService: ParentService = (parentService as ParentService | undefined) ?? "iva";
        setAnalyticsIdentity({
          parentService: resolvedParentService,
          referenceType: resolveReferenceType(resolvedParentService),
          referenceId: resolvedReferenceId,
        });
        setAnalyticsEnabled(analyticsEnabled === true);
        trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.SESSION_STARTED, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INIT });

        if (!payload || !payload.sessionToken) {
          logger.error("[RegulaLegalIdVerification] IDME_INIT received without a session token");
          if (allowedOrigin) {
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode: "session_invalid", message: "Invalid initialization payload" },
            } as VerificationErrorMessage, origin);
          }
          trackTerminalFailure("initializing", "session_invalid");
          setFlowState("error");
          setError("Invalid initialization payload");
          return;
        }

        if (typeof tokenExpiresAt === "number") {
          tokenExpiresAtRef.current = tokenExpiresAt;
        }
        setFallbackAvailable(initFallbackAvailable === true);

        window.removeEventListener("message", handleInitMessage);

        // Guard: abort if the token won't last another 2 minutes. Unlike Veriff there is no POST
        // here, but the token is required for the upcoming submit, so fail fast.
        if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
          logger.warn("[RegulaLegalIdVerification] Token expiring soon, aborting");
          trackTerminalFailure("initializing", "token_expired");
          setFlowState("error");
          setError("Session token has expired");
          if (allowedOrigin) {
            window.parent.postMessage({
              source: "idme-verification",
              type: "VERIFICATION_ERROR",
              payload: { errorCode: "token_expired", message: "Session token has expired", failedApi: "post_legal_id_failed" as const },
            } as VerificationErrorMessage, origin);
          }
          return;
        }

        // UPLOAD model: no session is created up front. Store the token and show the intro screen;
        // the POST happens later in submitCapture once images are captured.
        setSessionToken(token);
        logger.log("[RegulaLegalIdVerification] Token received; ready to capture");
        setFlowState("ready");
      };

      window.addEventListener("message", handleInitMessage);

      // Signal to parent that we're ready to receive the token
      window.parent.postMessage({
        source: "idme-verification",
        type: "IDME_READY",
      }, origin);

      return () => {
        window.removeEventListener("message", handleInitMessage);
      };
    } catch (err) {
      logger.error("[RegulaLegalIdVerification] Initialization failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";

      trackFlowError(err, "initialization_failed", "legal_id_verification", {
        endpoint: "POST /v1/verifications/legal-ids",
        failedApi: "post_legal_id_failed",
        sessionTokenPresent: !!sessionToken,
      });

      // Sentry-only — same parent-origin rationale as useLegalIdVerification.ts.
      setFlowState("error");
      setError(errorMessage);
      return () => {};
    }
  };

  const sendSuccessToParent = useCallback((payload: DocumentSuccessPayload) => {
    if (!parentOrigin) {
      logger.error("[RegulaLegalIdVerification] Cannot send message - parent origin not set");
      return;
    }

    const message: VerificationCompleteMessage = {
      source: "idme-verification",
      type: "VERIFICATION_COMPLETE",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const sendErrorToParent = useCallback((payload: DocumentErrorPayload) => {
    if (!parentOrigin) {
      logger.error("[RegulaLegalIdVerification] Cannot send error - parent origin not set");
      return;
    }

    const message: VerificationErrorMessage = {
      source: "idme-verification",
      type: "VERIFICATION_ERROR",
      payload,
    };
    window.parent.postMessage(message, parentOrigin);
  }, [parentOrigin]);

  const handleVerificationError = useCallback((
    errorCode: DocumentErrorPayload["errorCode"],
    message: string,
    statusCode?: number,
    responseData?: unknown,
    failedApi?: DocumentFailedApi,
  ) => {
    trackTerminalFailure(stateRef.current, errorCode);
    setFlowState("error");
    setError(message);
    logger.error(`[RegulaLegalIdVerification] Error [${errorCode}]${statusCode ? ` (HTTP ${statusCode})` : ""}:`, message);

    sendErrorToParent({
      errorCode,
      message,
      statusCode,
      failedApi,
      data: responseData,
    });
  }, [sendErrorToParent]);

  const pollVerificationStatus = useCallback(async (overrideVerificationId?: string, overrideSessionToken?: string) => {
    const activeVerificationId = overrideVerificationId ?? verificationId;
    const activeToken = overrideSessionToken ?? sessionToken;
    if (!activeVerificationId || !activeToken) {
      handleVerificationError("network_error", "Missing verification ID or session token");
      return;
    }

    setFlowState("processing");

    try {
      logger.log("[RegulaLegalIdVerification] Polling verification status...");

      addAPIBreadcrumb(`GET /v1/verifications/legal-ids/${activeVerificationId}`, "polling_start", {});

      // Exponential backoff polling — 60s timeout
      // Intervals: 2s → 3s → 4.5s → 6.75s → 10s → 15s → 15s... (capped at 15s)
      const initialInterval = 2000;
      const multiplier = 1.5;
      const maxInterval = 15000;
      const timeoutMs = 60000;
      const startTime = Date.now();
      let attempt = 0;
      let lastStatusCode: number | undefined = undefined;

      while (true) {
        attempt++;

        // Guard: abort if the token won't last another 2 minutes
        if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
          logger.warn("[RegulaLegalIdVerification] Token expiring soon, aborting poll");
          handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "get_legal_id_failed");
          return;
        }

        try {
          const { data: result, statusCode } = await getLegalIdVerificationStatus(
            activeVerificationId,
            activeToken
          );
          lastStatusCode = statusCode;

          logger.log("[RegulaLegalIdVerification] Poll attempt", attempt, "status:", result?.status, "outcome:", result?.outcome);

          addAPIBreadcrumb(`GET /v1/verifications/legal-ids/${activeVerificationId}`, statusCode, {
            attempt,
            elapsed: Date.now() - startTime,
            status: result?.status,
            outcome: result?.outcome,
          });

          // Document rejected by provider — treat as verification error, not success
          if (result?.outcome === "failed" || result?.outcome === "fraudulent") {
            logger.log("[RegulaLegalIdVerification] Document rejected, outcome:", result.outcome);
            handleVerificationError("doc_rejected", "Document verification was rejected", statusCode, result, "get_legal_id_failed");
            return;
          }

          // Verification passed — show confirmation screen before sending to parent
          if (result?.outcome === "passed") {
            setVerificationResult({ data: result });
            setFlowState("confirming");
            return;
          }

          // Check elapsed time before waiting for next attempt
          const elapsed = Date.now() - startTime;
          if (elapsed >= timeoutMs) {
            logger.log("[RegulaLegalIdVerification] Polling timeout reached after", elapsed, "ms");
            handleVerificationError("network_error", "Verification is still processing. Please try again later.", lastStatusCode, undefined, "get_legal_id_failed");
            return;
          }

          // Exponential backoff: 2s → 3s → 4.5s → ... capped at 15s
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          logger.log("[RegulaLegalIdVerification] Waiting", interval, "ms before next poll (attempt", attempt, ")");
          await new Promise(resolve => setTimeout(resolve, interval));

        } catch (pollError) {
          const statusCode = pollError instanceof ApiError ? pollError.statusCode : undefined;
          logger.warn("[RegulaLegalIdVerification] Poll attempt", attempt, "failed:", statusCode ? `HTTP ${statusCode}` : "network error", pollError);

          // 401 and 400 — fail immediately, don't retry
          if (statusCode === 401 || statusCode === 400) {
            throw pollError;
          }

          const elapsed = Date.now() - startTime;
          if (elapsed >= timeoutMs) {
            logger.log("[RegulaLegalIdVerification] Polling timeout reached after error, elapsed:", elapsed, "ms");
            throw Object.assign(new Error("Network error: unable to retrieve verification status"), { statusCode });
          }

          // Exponential backoff before retry (5xx and network errors are retried)
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      }
    } catch (err) {
      logger.error("[RegulaLegalIdVerification] Failed to get verification status:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to verify status";
      const statusCode = err instanceof ApiError ? err.statusCode : (err as any)?.statusCode as number | undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;

      handleVerificationError(errorCodeFromStatus(statusCode), errorMessage, statusCode, responseData, "get_legal_id_failed");
    }
  }, [verificationId, sessionToken, handleVerificationError]);

  const submitCapture = useCallback(async (images: { imageFront: string | null; imageBack: string | null }) => {
    const token = sessionToken;
    if (!token) {
      handleVerificationError("session_invalid", "Missing session token for submit", undefined, undefined, "post_legal_id_failed");
      return;
    }

    // Guard: abort if the token won't last another 2 minutes
    if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      logger.warn("[RegulaLegalIdVerification] Token expiring soon, aborting submit");
      handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "post_legal_id_failed");
      return;
    }

    if (!images.imageFront && !images.imageBack) {
      handleVerificationError("doc_capture_failed", "No document image was captured");
      return;
    }

    // Leave the capturing screen immediately; the create + poll run under the processing screen.
    setFlowState("processing");

    try {
      logger.log("[RegulaLegalIdVerification] Submitting captured images (UPLOAD)");
      addAPIBreadcrumb("POST /v1/verifications/legal-ids", "pending", { flow: "legal_id_verification" });

      const { data } = await submitRegulaLegalIdVerification({
        sessionToken: token,
        imageFront: images.imageFront ?? "",
        imageBack: images.imageBack ?? undefined,
        externalReferenceId: externalReferenceIdRef.current ?? undefined,
      });

      logger.log("[RegulaLegalIdVerification] Images submitted:", { verificationId: data.uuid, status: data.status });
      setVerificationId(data.uuid);

      // athena processes Regula asynchronously — poll the shared status endpoint to a terminal outcome.
      // Pass the fresh id/token explicitly: the verificationId state update hasn't propagated yet.
      await pollVerificationStatus(data.uuid, token);
    } catch (err) {
      logger.error("[RegulaLegalIdVerification] Image submit failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      const statusCode = err instanceof ApiError ? err.statusCode : undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;

      trackFlowError(err, "session_creation_failed", "legal_id_verification", {
        endpoint: "POST /v1/verifications/legal-ids",
        failedApi: "post_legal_id_failed",
        sessionTokenPresent: !!token,
        httpStatus: statusCode,
        responseData: responseData ?? null,
      });

      handleVerificationError(errorCodeFromStatus(statusCode), errorMessage, statusCode, responseData, "post_legal_id_failed");
    }
  }, [sessionToken, pollVerificationStatus, handleVerificationError]);

  const confirmVerification = useCallback(async () => {
    if (!verificationId || !sessionToken || !verificationResult) {
      handleVerificationError("network_error", "Missing verification data for confirmation");
      return;
    }

    // Guard: abort if the token won't last another 2 minutes
    if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      logger.warn("[RegulaLegalIdVerification] Token expiring soon, aborting confirmation");
      handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "patch_legal_id_failed");
      return;
    }

    try {
      logger.log("[RegulaLegalIdVerification] User confirmed — calling PATCH");
      await patchLegalIdVerification(verificationId, sessionToken);
      trackOutcomePassed(DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION);
      setFlowState("completed");
      sendSuccessToParent(verificationResult);
    } catch (err) {
      logger.error("[RegulaLegalIdVerification] PATCH confirmation failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to confirm verification";
      const statusCode = err instanceof ApiError ? err.statusCode : undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;

      trackFlowError(err, "confirmation_failed", "legal_id_verification", {
        endpoint: `PATCH /v1/verifications/legal-ids/${verificationId}`,
        failedApi: "patch_legal_id_failed",
        verificationId: verificationId || "unknown",
        sessionTokenPresent: !!sessionToken,
        httpStatus: statusCode,
        responseData: responseData ?? null,
      });

      handleVerificationError(errorCodeFromStatus(statusCode), errorMessage, statusCode, undefined, "patch_legal_id_failed");
    }
  }, [verificationId, sessionToken, verificationResult, sendSuccessToParent, handleVerificationError]);

  return {
    // State
    state,
    error,
    verificationId,
    sessionToken,
    verificationResult,
    fallbackAvailable,

    // State setters
    setState: setFlowState,

    // Methods
    initializeRegulaLegalIdVerification,
    submitCapture,
    pollVerificationStatus,
    confirmVerification,
    handleVerificationError,
  };
}
